import { Button } from '@/components/ui/button';
import { Shield, Store } from 'lucide-react';

const heroImage = 'https://images.unsplash.com/photo-1605296867304-46d5465a13f1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3MjQyMTd8MHwxfHNlYXJjaHwxfHxneW0lMjB3b3Jrb3V0fGVufDB8fHx8MTc2ODE0OTE4Nnww&ixlib=rb-4.1.0&q=85&w=1920';

export const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center pt-32 md:pt-40 pb-20 overflow-hidden">
      {/* Background Image with Premium Purple Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={heroImage}
          alt="Athlete training"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a20]/95 via-[#2d1035]/90 to-[#1a0a20]/85" />
      </div>

      {/* Soft Purple Glow Effects */}
      <div className="absolute top-1/4 right-1/4 w-[400px] h-[400px] bg-primary/20 rounded-full blur-[150px] animate-pulse-soft" />
      <div className="absolute bottom-1/3 left-1/5 w-[300px] h-[300px] bg-gold/10 rounded-full blur-[120px] animate-pulse-soft" style={{ animationDelay: '1s' }} />
      
      {/* Floating Accent Shapes */}
      <div className="absolute top-20 right-20 w-20 h-20 border border-primary/20 rounded-full animate-float opacity-30" />
      <div className="absolute bottom-40 left-16 w-12 h-12 border border-gold/20 rounded-full animate-float opacity-20" style={{ animationDelay: '2s' }} />

      {/* Grain Texture Overlay */}
      <div className="absolute inset-0 texture-grain" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl">
          {/* Trust Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-green-500/15 border border-green-500/30 text-green-400 text-sm font-medium mb-8 backdrop-blur-sm animate-fade-up">
            <Shield className="w-4 h-4" strokeWidth={1.5} />
            <span>100% Authentic & Certified Products</span>
          </div>

          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-display font-bold text-white leading-tight mb-6 animate-fade-up stagger-1">
            Certified. Authentic.
            <span className="block text-gold">
              Performance-Driven
            </span>
            Supplements.
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-white/70 leading-relaxed mb-10 max-w-xl animate-fade-up stagger-2">
            Your trusted destination for genuine fitness nutrition, trusted by athletes and fitness professionals across India & Nepal.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4 mb-12 animate-fade-up stagger-3">
            <Button 
              size="xl" 
              className="btn-whatsapp bg-green-500 hover:bg-green-600 text-white border-0 px-8 shadow-lg shadow-green-500/20 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-green-500/30 active:scale-95"
              asChild
            >
              <a href="https://wa.me/9779824042067" target="_blank" rel="noopener noreferrer">
                <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current mr-2">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                </svg>
                Order on WhatsApp
              </a>
            </Button>
            <Button 
              size="xl" 
              className="bg-white/5 hover:bg-white/10 text-white border border-white/20 px-8 backdrop-blur-sm transition-all hover:-translate-y-0.5"
              asChild
            >
              <a href="/stores">
                <Store className="w-5 h-5 mr-2" strokeWidth={1.5} />
                Visit Our Store
              </a>
            </Button>
          </div>

          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 animate-fade-up stagger-4">
            {[
              { value: '5000+', label: 'Happy Customers' },
              { value: '100%', label: 'Authentic Products' },
              { value: '50+', label: 'Premium Brands' },
              { value: '2', label: 'Store Locations' },
            ].map((stat) => (
              <div 
                key={stat.label}
                className="text-center md:text-left"
              >
                <div className="text-3xl md:text-4xl font-display font-bold text-gold">
                  {stat.value}
                </div>
                <div className="text-sm text-white/50">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <div className="w-7 h-12 rounded-full border border-white/20 flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-gold/80 rounded-full" />
        </div>
      </div>

      {/* Bottom Section Separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
    </section>
  );
};
